// src/services/api/constraintAPI.js
import { createAuthHeaders } from '../../utils/authUtils';

const FIREBASE_FUNCTIONS_BASE_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_BASE_URL;

/**
 * Client-side API service for schedule constraint operations.
 * Calls Firebase Functions backend.
 */
class ConstraintAPI {

  /**
   * Get all active constraints, optionally filtered by date range or crew member.
   * @param {Object} options
   * @param {string} [options.startDate] - 'YYYY-MM-DD'
   * @param {string} [options.endDate]   - 'YYYY-MM-DD'
   * @param {string} [options.crewMemberId]
   * @returns {Promise<Array>}
   */
  static async getAll({ startDate, endDate, crewMemberId } = {}) {
    try {
      const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/constraints`);
      url.searchParams.set('operation', 'getAll');
      if (startDate)    url.searchParams.set('startDate', startDate);
      if (endDate)      url.searchParams.set('endDate', endDate);
      if (crewMemberId) url.searchParams.set('crewMemberId', crewMemberId);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: await createAuthHeaders()
      });

      if (!response.ok) {
        await this._handleAPIError(response, 'getAll');
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'API request was not successful');
      }

      return result.data || [];
    } catch (error) {
      console.error('ConstraintAPI.getAll error:', error.message);
      throw new Error(`Failed to fetch constraints: ${error.message}`);
    }
  }

  /**
   * Create a new schedule constraint.
   * @param {Object} constraintData
   * @returns {Promise<Object>} Created constraint
   */
  static async create(constraintData) {
    try {
      const response = await fetch(`${FIREBASE_FUNCTIONS_BASE_URL}/constraints`, {
        method: 'POST',
        headers: await createAuthHeaders(),
        body: JSON.stringify({ operation: 'create', constraintData })
      });

      if (!response.ok) {
        await this._handleAPIError(response, 'create');
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'API request was not successful');
      }

      return result.data;
    } catch (error) {
      console.error('ConstraintAPI.create error:', error.message);
      throw error;
    }
  }

  /**
   * Update an existing constraint.
   * @param {string} constraintId
   * @param {Object} updates
   * @returns {Promise<Object>} Updated constraint
   */
  static async update(constraintId, updates) {
    try {
      const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/constraints`);
      url.searchParams.set('operation', 'update');
      url.searchParams.set('constraintId', constraintId);

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: await createAuthHeaders(),
        body: JSON.stringify({ updates })
      });

      if (!response.ok) {
        await this._handleAPIError(response, 'update');
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'API request was not successful');
      }

      return result.data;
    } catch (error) {
      console.error('ConstraintAPI.update error:', error.message);
      throw error;
    }
  }

  /**
   * Delete a constraint permanently.
   * @param {string} constraintId
   * @returns {Promise<void>}
   */
  static async delete(constraintId) {
    try {
      const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/constraints`);
      url.searchParams.set('operation', 'delete');
      url.searchParams.set('constraintId', constraintId);

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: await createAuthHeaders(),
        body: JSON.stringify({})
      });

      if (!response.ok) {
        await this._handleAPIError(response, 'delete');
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'API request was not successful');
      }
    } catch (error) {
      console.error('ConstraintAPI.delete error:', error.message);
      throw error;
    }
  }

  /**
   * Exclude a specific date from a weekly constraint (suppress one occurrence).
   * @param {string} constraintId
   * @param {string} date - 'YYYY-MM-DD'
   * @returns {Promise<Object>} Updated constraint
   */
  static async excludeDate(constraintId, date) {
    try {
      const url = new URL(`${FIREBASE_FUNCTIONS_BASE_URL}/constraints`);
      url.searchParams.set('operation', 'excludeDate');
      url.searchParams.set('constraintId', constraintId);

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: await createAuthHeaders(),
        body: JSON.stringify({ date })
      });

      if (!response.ok) {
        await this._handleAPIError(response, 'excludeDate');
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'API request was not successful');
      }

      return result.data;
    } catch (error) {
      console.error('ConstraintAPI.excludeDate error:', error.message);
      throw error;
    }
  }

  static async _handleAPIError(response, operation) {
    try {
      const data = await response.json();
      const err = new Error(data.message || data.error || 'Unknown error');
      err.errorCode = data.errorCode;
      err.details = data.details;
      throw err;
    } catch (jsonError) {
      if (jsonError.errorCode) {
        throw jsonError;
      }
      throw new Error(`${operation} failed: HTTP ${response.status} - ${response.statusText}`);
    }
  }
}

export { ConstraintAPI };
